import { DriveStore } from "../store/reducers";
import * as Sentry from "@sentry/browser";
import * as errorHandler from "../utils/error";

export interface IUrl {
    href: string;
    pathname: string;
    hash: string;
    host: string;
    hostname: string;
}

/**
 * We need to have the ability to manually send up errors to Sentry for the utilities in this file. Generally, we
 * don't want to do this but in this case it is necessary because it can potentially occur before our standard error
 * handling code is set up.
 *
 * Outside of the utilities present in this file, we shouldn't be using this.
 */
function sendToErrorTracker(error: Error, tags: string[], store?: DriveStore) {
    let errorToSend = error;
        Sentry.withScope((scope) => {
            scope.setTag("type", ["app/utils/databaseNameParsingUtils"].concat(tags).join(","));
            const version = window.geotabModules?.device.version;
            version && scope.setTag("app.release", version);
            if (store) {
                scope.setTag("database", store.getState().data.lastDatabase);
            }
            errorToSend.message = errorHandler.cleanupSensitiveString(error.message);
            Sentry.captureException(errorToSend);
        });
}

function decodeUri(uri: string): string {
    let prevUri = uri;
    let decodedUri = decodeURIComponent(uri);
    while (decodedUri !== prevUri) {
        prevUri = decodedUri;
        decodedUri = decodeURIComponent(decodedUri);
    }
    return decodedUri;
}

/**
 * Tries to grab the database name from credentials that are provided as a URL parameter. For example something in the form of
 * 'my.geotab.com/drive/#login,(credentials:('userName': 'someUser', database: 'myDatabase', 'sessionId': 'abcdxyz'))' would be able to
 * be parsed and return the 'database' value from the credentials string.
 */
function getDatabaseNameFromHash(url: IUrl): string {
    // If credentials from remote are in uri, parse and use for database
    let uri = !!url.hash && decodeUri(url.hash);
    if (uri && uri.indexOf("credentials") > -1) {
        let cleanedUri = uri.replace(/[\"\'\`]/g, "");
        let regexDatabaseObject = (/database:[^,)]*/).exec(cleanedUri);
        if (regexDatabaseObject && regexDatabaseObject[0]) {
            let databaseArray = regexDatabaseObject[0].split(":");
            let databaseNameFromState = (databaseArray[1] || "").replace(/('|")/g, "");
            if (databaseNameFromState) {
                return databaseNameFromState.toLowerCase();
            }
        }
    }
    return "";
}

/**
 * Tries to grab the database name from the Redux 'data' store's 'lastDatabase' parameter.
 */
function getDatabaseNameFromStorage(store: DriveStore): string {
    let databaseNameFromStorage = store.getState().data.lastDatabase;
    if (databaseNameFromStorage) {
        return databaseNameFromStorage.toLowerCase();
    }
    return "";
}

/**
 * Tries to grab the database name from credentials stored in Redux. If available, grabs the most recently active user
 * from Redux and checks to see if credentials are available for them. If so, returns the `database` value that is stored
 * under the user's credentials in Redux.
 */
function getDatabaseNameFromStoredCredentials(store: DriveStore): string {
    const activeUsersStored = store.getState().activeUsers as DeepReadonlyArray<string>;
    const activeUsers = activeUsersStored ?? [];

    const currentUser = activeUsers[0];
    if (currentUser) {
        const credentials = store.getState().driveAPICredentials[currentUser];
        return credentials && credentials.credentials && (credentials.credentials.database || "").toLowerCase();
    }
    return "";
}

/**
 * Tries to grab the database name directly from the `location` value. For example something in the form of
 * 'my.geotab.com/myDatabase/drive' would return 'myDatabase'.
 * @param includeDrive Whether or not we want to return the value we parse if it is 'drive'
 */
export function getDatabaseNameFromUri(url: IUrl, includeDrive = false, store?: DriveStore) {
    try {
        let databaseNameFromUri = (decodeUri(url.pathname).split("/")[1] || "").toLowerCase();
        if (includeDrive || databaseNameFromUri !== "drive") {
            return databaseNameFromUri;
        }
        return "";
    } catch (error) {
        sendToErrorTracker(error, ["dbnameparse"], store);
    }

    return "";
}

/**
 * Attempts to parse & return the current database through various alternatives to the 'location' value. Checks for the
 * presence of credentials passed as a URL parameter. If the Redux store is provided to this method, also checks credentials
 * stored in Redux and the redux 'lastDatabase' parameter
 * @param store Optionally pass the Redux store, allowing us to check a wider variety of places as noted above.
 * @returns An object containing the database name, and a message to be logged in the case that we are checking if the
 * user needs to be redirected
 */
export function getDatabaseNameFromNonUriSource(url: IUrl, store?: DriveStore): { databaseName: string; message: string } | null {
    let isNotRedirectServer = () => {
        let subDomain = url.hostname.split(".")[0];
        let subDomainNumberRegex = subDomain.match(/\d+$/);
        return !!subDomainNumberRegex && subDomainNumberRegex[0] !== "0";
    };

    try {
        let databaseName = getDatabaseNameFromHash(url);
        if (databaseName) {
            return { databaseName, message: `Redirect from db name in URI ${ location.host } ${ location.pathname } ${ databaseName }`};
        } else if (!store) {
            return null;
        } else {
            databaseName = getDatabaseNameFromStoredCredentials(store);
            if (databaseName) {
                return { databaseName, message: `Redirect from db name in stored credentials ${ databaseName }`};
            }
            databaseName = getDatabaseNameFromStorage(store);
            if (databaseName && isNotRedirectServer()) {
                return { databaseName, message: `Redirect from db name in storage ${ databaseName }`};
            }
        }
    } catch (error) {
        sendToErrorTracker(error, ["dbnameparse"], store);
    }

    return null;
}